import { lazy } from 'react';

// ============================================================
// 路由代码块统一定义 + 链接预取
//
// 每个页面只保留一个 loader，React.lazy 和预取共用它：
// 鼠标移到链接上（或手指按下）时，先下载目标页面的代码块；
// 真正点击时直接命中缓存，避免先闪通用占位骨架、再显示页面骨架。
// Home 仍在 App.jsx 静态导入，因为它是首屏页面。
// ============================================================

const loadStore = () => import('../pages/Store');
const loadGameDetail = () => import('../pages/GameDetail');
const loadCommunity = () => import('../pages/Community');
const loadPostDetail = () => import('../pages/PostDetail');
const loadProfile = () => import('../pages/Profile');
const loadLibrary = () => import('../pages/Library');
const loadCart = () => import('../pages/Cart');
const loadCreator = () => import('../pages/Creator');
const loadWallet = () => import('../pages/Wallet');
const loadMapStore = () => import('../pages/MapStore');
const loadNewMap = () => import('../pages/NewMap');
const loadMyWorks = () => import('../pages/MyWorks');
const loadMapSettings = () => import('../pages/MapSettings');
const loadCodeMarket = () => import('../pages/CodeMarket');
const loadShareView = () => import('../pages/ShareView');
const loadAdmin = () => import('../pages/Admin');
const loadMessageReports = () => import('../pages/MessageReports');
const loadRules = () => import('../pages/Rules');
const loadPrivacy = () => import('../pages/Privacy');
const loadSettings = () => import('../pages/Settings');
const loadMessages = () => import('../pages/Messages');
const loadNotifications = () => import('../pages/Notifications');
const loadLevelBenefits = () => import('../pages/LevelBenefits');
const loadActivities = () => import('../pages/Activities');
const loadLogin = () => import('../pages/Login');
const loadRegister = () => import('../pages/Register');
const loadLoginRecords = () => import('../pages/LoginRecords');
const loadSocial = () => import('../pages/Social');
const loadMails = () => import('../pages/Mails');
const loadFeedback = () => import('../pages/Feedback');
const loadDaily = () => import('../pages/Daily');
const loadAchievements = () => import('../pages/Achievements');
const loadCustomerService = () => import('../pages/CustomerService');
const loadTestPage = () => import('../pages/TestPage');
const loadChangelog = () => import('../pages/Changelog');
const loadInteractiveEditor = () => import('../pages/InteractiveEditor');
const loadDownloads = () => import('../pages/Downloads');

export const Store = lazy(loadStore);
export const GameDetail = lazy(loadGameDetail);
export const Community = lazy(loadCommunity);
export const PostDetail = lazy(loadPostDetail);
export const Profile = lazy(loadProfile);
export const Library = lazy(loadLibrary);
export const Cart = lazy(loadCart);
export const Creator = lazy(loadCreator);
export const Wallet = lazy(loadWallet);
export const MapStore = lazy(loadMapStore);
export const NewMap = lazy(loadNewMap);
export const MyWorks = lazy(loadMyWorks);
export const MapSettings = lazy(loadMapSettings);
export const CodeMarket = lazy(loadCodeMarket);
export const ShareView = lazy(loadShareView);
export const Admin = lazy(loadAdmin);
export const MessageReports = lazy(loadMessageReports);
export const Rules = lazy(loadRules);
export const Privacy = lazy(loadPrivacy);
export const Settings = lazy(loadSettings);
export const Messages = lazy(loadMessages);
export const Notifications = lazy(loadNotifications);
export const LevelBenefits = lazy(loadLevelBenefits);
export const Activities = lazy(loadActivities);
export const Login = lazy(loadLogin);
export const Register = lazy(loadRegister);
export const LoginRecords = lazy(loadLoginRecords);
export const Social = lazy(loadSocial);
export const Mails = lazy(loadMails);
export const Feedback = lazy(loadFeedback);
export const Daily = lazy(loadDaily);
export const Achievements = lazy(loadAchievements);
export const CustomerService = lazy(loadCustomerService);
export const TestPage = lazy(loadTestPage);
export const Changelog = lazy(loadChangelog);
export const InteractiveEditor = lazy(loadInteractiveEditor);
export const Downloads = lazy(loadDownloads);

const exact = (path, load) => ({ test: p => p === path, load });
const pattern = (regexp, load) => ({ test: p => regexp.test(p), load });

// 顺序敏感：具体路径必须排在宽泛路径前面。
const PREFETCH_RULES = [
  exact('/store', loadStore),
  exact('/community', loadCommunity),
  exact('/library', loadLibrary),
  exact('/cart', loadCart),
  exact('/downloads', loadDownloads),
  exact('/creator', loadCreator),
  exact('/wallet', loadWallet),
  exact('/maps', loadMapStore),
  exact('/my-works', loadMyWorks),
  exact('/code-market', loadCodeMarket),
  exact('/admin', loadAdmin),
  exact('/admin/msg-reports', loadMessageReports),
  exact('/rules', loadRules),
  exact('/privacy', loadPrivacy),
  exact('/settings', loadSettings),
  exact('/messages', loadMessages),
  exact('/notifications', loadNotifications),
  exact('/level-benefits', loadLevelBenefits),
  exact('/activities', loadActivities),
  exact('/login', loadLogin),
  exact('/register', loadRegister),
  exact('/login-records', loadLoginRecords),
  exact('/social', loadSocial),
  exact('/mails', loadMails),
  exact('/feedback', loadFeedback),
  exact('/daily', loadDaily),
  exact('/achievements', loadAchievements),
  exact('/customer-service', loadCustomerService),
  exact('/test-page', loadTestPage),
  exact('/changelog', loadChangelog),
  pattern(/^\/community\/post\/[^/]+$/, loadPostDetail),
  pattern(/^\/profile\/[^/]+$/, loadProfile),
  pattern(/^\/game\/[^/]+$/, loadGameDetail),
  pattern(/^\/shared\/[^/]+$/, loadShareView),
  pattern(/^\/social\/[^/]+$/, loadSocial),
  pattern(/^\/maps\/new$/, loadNewMap),
  pattern(/^\/maps\/edit\/[^/]+$/, loadNewMap),
  pattern(/^\/maps\/settings\/[^/]+$/, loadMapSettings),
  pattern(/^\/maps\/[^/]+$/, loadGameDetail),
  pattern(/^\/interactive\/new$/, loadInteractiveEditor),
  pattern(/^\/interactive\/edit\/[^/]+$/, loadInteractiveEditor),
];

const prefetchedPaths = new Set();

export function prefetchRoute(pathname) {
  if (!pathname) return;
  const path = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
  if (path === '/' || prefetchedPaths.has(path)) return;
  const rule = PREFETCH_RULES.find(item => item.test(path));
  if (!rule) return;
  prefetchedPaths.add(path);
  try {
    rule.load().catch(() => {});
  } catch (_) {
    // 预取失败不影响真正的路由跳转；跳转时 React.lazy 会再次加载。
  }
}

// 同时兼容 BrowserRouter 的 /store 与 HashRouter 的 #/store。
export function prefetchByHref(href) {
  if (!href) return;
  let path = href;
  const hashIndex = path.indexOf('#');
  if (hashIndex >= 0) {
    const hashPath = path.slice(hashIndex + 1);
    if (!hashPath.startsWith('/')) return;
    path = hashPath;
  }
  if (!path.startsWith('/')) return;
  prefetchRoute(path);
}
